declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let recaptchaLoader: Promise<void> | null = null;

function loadRecaptchaScript(siteKey: string) {
  if (window.grecaptcha) {
    return Promise.resolve();
  }

  if (recaptchaLoader) {
    return recaptchaLoader;
  }

  recaptchaLoader = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById("recaptcha-v3-script") as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Could not load security verification.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = "recaptcha-v3-script";
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load security verification."));

    document.head.appendChild(script);
  });

  return recaptchaLoader;
}

export async function executeRecaptcha(action: string) {
  if (typeof window === "undefined") {
    throw new Error("Security verification is only available in the browser.");
  }

  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    throw new Error("Security verification is not configured.");
  }

  await loadRecaptchaScript(siteKey);

  if (!window.grecaptcha) {
    throw new Error("Security verification is unavailable right now.");
  }

  await new Promise<void>((resolve) => {
    window.grecaptcha?.ready(() => resolve());
  });

  const token = await window.grecaptcha.execute(siteKey, { action });
  if (!token) {
    throw new Error("Security verification failed. Please try again.");
  }

  return token;
}

export {};
